// DoctorFAQForm.tsx
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import {
  PagerTargetEvent,
  HeaderThElement,
} from "@progress/kendo-react-data-tools";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import IconButton from "../../components/common/IconButton";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import ShadowCard from "../../components/common/ShadowCard";
import AlertBox from "../../components/common/AlertBox";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import FormTextArea from "../../components/formFields/FormTextArea";
import ExpandableText from "../../components/common/ExpandableText";
import { MdDelete, MdEdit } from "react-icons/md";
import {
  deleteDoctorFAQ,
  getDoctorFAQByDoctorID,
  insertDoctorFAQ,
  updateDoctorFAQ,
} from "./services/doctor.service";
import { clearDoctorFAQs } from "./doctorSlice";

interface PageState {
  skip: number;
  take: number;
}
const initialDataState: PageState = { skip: 0, take: 10 };

const DoctorFAQForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const DoctorID = location.state?.DoctorID;

  const [editItem, setEditItem] = useState<any>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [formKey, setFormKey] = useState(1);
  const [page, setPage] = useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = useState<
    number | string | undefined
  >();

  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const faqs = useAppSelector((state) => state.doctor.DoctorFAQs);
  const loading = useAppSelector((state) => state.doctor.loading);
  const formLoading = useAppSelector((state) => state.doctor.formLoading);

  useEffect(() => {
    setFormKey((p) => p + 1);
  }, [faqs, editItem]);
  useEffect(() => {
    if (DoctorID) dispatch(getDoctorFAQByDoctorID(DoctorID));
    return () => {
      dispatch(clearDoctorFAQs());
    };
  }, [DoctorID]);

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take = targetEvent.value === "All" ? faqs.length : event.page.take;
    if (targetEvent.value) setPageSizeValue(targetEvent.value);
    setPage({ ...event.page, take });
  };

  const handleAdd = async (values: any) => {
    const response = await dispatch(
      insertDoctorFAQ({
        DoctorID,
        Question: values.Question,
        Answer: values.Answer,
      }),
    );
    if (response?.meta?.requestStatus === "fulfilled") {
      dispatch(getDoctorFAQByDoctorID(DoctorID));
      setEditItem(null);
    }
  };

  const handleUpdate = async (values: any) => {
    const response = await dispatch(
      updateDoctorFAQ({
        ID: editItem.ID,
        DoctorID,
        Question: values.Question,
        Answer: values.Answer,
      }),
    );
    if (response?.meta?.requestStatus === "fulfilled") {
      dispatch(getDoctorFAQByDoctorID(DoctorID));
      setEditItem(null);
    }
  };

  const handleDelete = async () => {
    if (deleteId) {
      const response = await dispatch(deleteDoctorFAQ(deleteId));
      if (response?.meta?.requestStatus === "fulfilled") {
        dispatch(getDoctorFAQByDoctorID(DoctorID));
        dispatch(closeDialog());
        setDeleteId(null);
      }
    }
  };

  const ActionCell = (props: any) => (
    <td
      className={props.className}
      style={{
        ...props.style,
        textAlign: "center",
        display: "flex",
        justifyContent: "space-evenly",
      }}
    >
      <IconButton
        type="button"
        fillMode="flat"
        size="small"
        title="Edit"
        onClick={() => setEditItem({ ...props.dataItem })}
        icon={MdEdit}
        iconClassName="absolute-position"
        iconStyle={{ fontSize: "20px" }}
      />
      <IconButton
        type="button"
        fillMode="flat"
        size="small"
        title="Delete"
        className="color-red"
        onClick={() => {
          dispatch(openDialog("deleteDoctorFAQ"));
          setDeleteId(props.dataItem.ID);
        }}
        icon={MdDelete}
        iconClassName="absolute-position"
        iconStyle={{ fontSize: "24px" }}
      />
    </td>
  );

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${props.index === 2 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );
  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <td {...(props as any).tdProps} colSpan={1} className={"table-cell"}>
      {props.children}
    </td>
  );

  return (
    <>
      {dialogName === "deleteDoctorFAQ" && (
        <AppDialog>
          <>
            <Typography.h5>Delete FAQ</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this FAQ?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{ display: "flex", justifyContent: "end", gap: "10px" }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={handleDelete}
                  type="button"
                  disabled={loading}
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}

      <GridLayout style={{ marginTop: 20 }} gap={{ rows: 10, cols: 10 }}>
        <GridLayoutItem colSpan={4}>
          <ShadowCard style={{ padding: 20 }}>
            <Typography.h4 style={{ margin: "0 0 20px 0", color: "#495057" }}>
              {editItem ? "Edit FAQ" : "Add FAQ"}
            </Typography.h4>
            <Form
              key={formKey}
              onSubmit={editItem ? handleUpdate : handleAdd}
              initialValues={editItem || { Question: "", Answer: "" }}
              render={(formRenderProps: FormRenderProps) => (
                <FormElement>
                  <GridLayout gap={{ rows: 15, cols: 20 }}>
                    <GridLayoutItem colSpan={4}>
                      <Field
                        name="Question"
                        label="Question"
                        placeholder="Enter question"
                        component={FormTextField}
                        validator={requiredValidator}
                        astrike={true}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem colSpan={4}>
                      <Field
                        name="Answer"
                        label="Answer"
                        placeholder="Enter answer"
                        component={FormTextArea}
                        rows={3}
                        validator={requiredValidator}
                        astrike={true}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem
                      colSpan={4}
                      style={{
                        display: "flex",
                        justifyContent: "end",
                        gap: "10px",
                      }}
                    >
                      {editItem && (
                        <Button
                          type="button"
                          fillMode={"outline"}
                          themeColor={"secondary"}
                          onClick={() => setEditItem(null)}
                        >
                          Cancel Edit
                        </Button>
                      )}
                      <ButtonWithLoading
                        label={editItem ? "Update FAQ" : "Add FAQ"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || formLoading}
                        loading={formLoading}
                      />
                    </GridLayoutItem>
                  </GridLayout>
                </FormElement>
              )}
            />
            <Typography.h4 style={{ margin: "0 0 20px 0", color: "#495057" }}>
              FAQs ({faqs?.length || 0})
            </Typography.h4>
            {faqs && faqs.length > 0 ? (
              <KendoGrid
                style={{ maxHeight: "calc(100vh - 400px)" }}
                data={faqs.slice(page.skip, page.take + page.skip)}
                skip={page.skip}
                take={page.take}
                total={faqs.length}
                pageable={{
                  buttonCount: 5,
                  pageSizes: [5, 10, "All"],
                  pageSizeValue,
                  type: "input",
                }}
                onPageChange={pageChange}
                cells={{ headerCell: HeaderCustomCell, data: MyDataCustomCell }}
              >
                <Column field="Question" title="Question" width={300} />
                <Column
                  field="Answer"
                  title="Answer"
                  cells={{
                    data: (props: any) => (
                      <td
                        style={{ ...props.style }}
                        className={props.className}
                      >
                        <ExpandableText text={props.dataItem?.Answer} />
                      </td>
                    ),
                  }}
                />
                <Column
                  field="ID"
                  title="Actions"
                  width={110}
                  cells={{ data: ActionCell }}
                  filterable={false}
                  locked={true}
                />
              </KendoGrid>
            ) : (
              <AlertBox />
            )}
          </ShadowCard>
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

export default DoctorFAQForm;
